import { Alert, Button, Card, Modal, useInitialFocus } from '@playerbase/ui';

type RemoveChannelConfirmDialogProps = {
  channelLabel: string;
  open: boolean;
  busy: boolean;
  errorMessage: string | null;
  onConfirm: () => void;
  onCancel: () => void;
};

// Confirms the destructive Remove action. Story guardrail says
// "already-captured Pool records remain intact and addressable" - so the
// copy here makes the consequence (stops new ingestion) and the
// non-consequence (existing records stay) explicit so the user can act
// without surprise. Cancel is auto-focused so Enter doesn't trigger the
// destructive primary by mistake. Mirrors DisconnectConfirmDialog.
export function RemoveChannelConfirmDialog({
  channelLabel,
  open,
  busy,
  errorMessage,
  onConfirm,
  onCancel,
}: RemoveChannelConfirmDialogProps): JSX.Element {
  const cancelRef = useInitialFocus<HTMLButtonElement>();

  return (
    <Modal
      open={open}
      onClose={onCancel}
      busy={busy}
      labelledById="remove-channel-title"
      initialFocusRef={cancelRef}
    >
      <Card>
        <Card.Header>
          <Card.Title id="remove-channel-title">Stop watching {channelLabel}?</Card.Title>
          <Card.Description>
            New messages from this channel will no longer flow into the Feedback
            Pool.
          </Card.Description>
        </Card.Header>
        <Card.Content>
          <p className="text-sm text-text-muted leading-relaxed">
            Existing Pool records that came from this channel stay where they are
            - they're still searchable and addressable. You can re-add the
            channel later to resume ingestion.
          </p>
          {errorMessage !== null && (
            <Alert tone="danger" className="mt-4">
              {errorMessage}
            </Alert>
          )}
        </Card.Content>
        <Card.Footer className="justify-end">
          <Button
            ref={cancelRef}
            variant="ghost"
            size="md"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button variant="danger" size="md" onClick={onConfirm} disabled={busy}>
            {busy ? 'Removing…' : 'Stop watching'}
          </Button>
        </Card.Footer>
      </Card>
    </Modal>
  );
}
